import { useState, useEffect, useCallback } from "react";
import axios from "../api/axios";
import dayjs from "dayjs";

export function useSitesPage() {
  const userId = Number(localStorage.getItem("user_id"));
  const role = localStorage.getItem("role");

  // --------------------------
  // STATE
  // --------------------------
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);

  // filters
  const [searchText, setSearchText] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // dialogs
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [editingSiteId, setEditingSiteId] = useState(null);

  // site form
  const [siteForm, setSiteForm] = useState({
    company: "",
    contractor: "",
    job_code: "",
    name: "",
    address: "",
    site_status: "not_start",
    remark: "",
  });

  // transfer form (handled by separate hook)
  const [transferForm, setTransferForm] = useState(null);

  // --------------------------
  // FETCH COMPANIES
  // --------------------------
  const fetchCompanies = useCallback(async () => {
    const res = await axios.get("/companies");
    const formatted = res.data.map((c) => ({
      ...c,
      created_at: c.created_at ? dayjs(c.created_at).format("YYYY-MM-DD HH:mm") : "",
    }));
    setCompanies(formatted);
  }, []);

  // --------------------------
  // FETCH PRODUCTS (catalog grouped)
  // --------------------------
  const fetchProducts = useCallback(async () => {
    const res = await axios.get("/productsByCatalog");
    setProducts(res.data || []);
  }, []);

  // --------------------------
  // FETCH USERS
  // --------------------------
  const fetchUsers = useCallback(async () => {
    const res = await axios.get("/users");
    setUsers(res.data || []);
  }, []);

  // --------------------------
  // FETCH SITES
  // --------------------------
  const fetchSites = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const res = await axios.get("/sites", { params });
        const formatted = res.data.map((s) => ({
          ...s,
          inventory_summary: s.inventory_summary?.filter((i) => i.amount !== 0),
          created_at: s.created_at ? dayjs(s.created_at).format("YYYY-MM-DD HH:mm") : "",
          updated_at: s.updated_at ? dayjs(s.updated_at).format("YYYY-MM-DD HH:mm") : "",
        }));
        setRows(formatted);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // --------------------------
  // INITIAL LOAD
  // --------------------------
  useEffect(() => {
    fetchCompanies();
    fetchProducts();
    fetchUsers();
    fetchSites();
  }, []);

  // --------------------------
  // FILTER HANDLERS
  // --------------------------
  const applyFilter = () => {
    const params = {};
    if (searchText) params.search = searchText;
    if (filterCompany) params.company_id = filterCompany;
    if (filterStatus) params.status = filterStatus;
    fetchSites(params);
  };

  const resetFilter = () => {
    setSearchText("");
    setFilterCompany("");
    setFilterStatus("");
    fetchSites({});
  };

  // --------------------------
  // SITE CRUD
  // --------------------------
  const openSiteDialog = (site = null) => {
    setEditingSiteId(site?.id ?? null);
    setSiteForm(
      site
        ? {
            company: site.company_id,
            contractor: site.contractor,
            job_code: site.job_code,
            name: site.name,
            address: site.address,
            site_status: site.site_status,
            remark: site.remark,
          }
        : {
            company: "",
            contractor: "",
            job_code: "",
            name: "",
            address: "",
            site_status: "not_start",
            remark: "",
          }
    );
    setSiteDialogOpen(true);
  };

  const saveSite = async () => {
    const payload = { ...siteForm };

    if (editingSiteId) {
      await axios.put(`/sites/${editingSiteId}`, payload);
    } else {
      await axios.post("/sites", payload);
    }

    setSiteDialogOpen(false);
    fetchSites();
  };

  const deleteSite = async (id) => {
    await axios.put(`/sites/${id}`, { active: false });
    setDeleteId(null);
    fetchSites();
  };

  return {
    userId,
    role,

    rows,
    companies,
    users,
    products,
    loading,

    searchText,
    setSearchText,
    filterCompany,
    setFilterCompany,
    filterStatus,
    setFilterStatus,

    applyFilter,
    resetFilter,

    siteDialogOpen,
    setSiteDialogOpen,
    openSiteDialog,
    siteForm,
    setSiteForm,
    saveSite,

    deleteId,
    setDeleteId,
    deleteSite,

    transferDialogOpen,
    setTransferDialogOpen,

    transferForm,
    setTransferForm,

    fetchSites,
    editingSiteId,
  };
}
